import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { callFunction } from "../api/client";
import { Spinner } from "./Spinner";

/* The two things a rep does to a record from its row, without opening it.
 *
 * docs/screens.md asks for these on the row, not down a menu, and gives the
 * reason: the people using this are between calls. Opening a record to press
 * one button and close it again is the cost this removes.
 *
 * Run web enrichment sends the record when it is pressed and says what
 * happened on one line under the row. James, 2026-10-23: no reason is asked
 * for, and the engine records who asked (api.queue_enrichment, engine
 * migration 281). A flag still needs a note, because the engine refuses a
 * flag with no note and whoever reads the flags needs to know what is wrong.
 *
 * "Add to a campaign" is in screens.md and is not here. A campaign is a set of
 * criteria the engine runs rather than a hand-picked list, so adding one record
 * to one is a different feature wearing that name. Dropped from the MVP by
 * James on 2026-09-11. */
export type RowAction = "enrich" | "flag" | null;

const FLAG_REASONS = [
  { key: "duplicate", label: "same as another record" },
  { key: "wrong_details", label: "details are wrong" },
  { key: "not_relevant", label: "does not belong in EVIE" },
  { key: "gone", label: "business or property is gone" },
  { key: "other", label: "something else" },
] as const;

export function RowActionForm({
  token, entityType, entityId, action, onDone,
}: {
  token: string;
  entityType: string;
  entityId: string;
  action: RowAction;
  onDone: () => void;
}) {
  if (action === "enrich") return <EnrichNow token={token} entityType={entityType} entityId={entityId} onDone={onDone} />;
  if (action === "flag") return <FlagForm token={token} entityType={entityType} entityId={entityId} onDone={onDone} />;
  return null;
}

function EnrichNow({ token, entityType, entityId, onDone }: {
  token: string; entityType: string; entityId: string; onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const sent = useRef(false);
  const run = useMutation({
    mutationFn: () => callFunction<boolean>(token, "queue_enrichment", {
      p_entity_type: entityType, p_entity_id: entityId,
    }),
    onSuccess: (accepted) => {
      queryClient.invalidateQueries({ queryKey: ["enrichment-page"] });
      queryClient.invalidateQueries({ queryKey: ["enrichment", entityType, entityId] });
      if (accepted) window.setTimeout(onDone, 2500);
    },
  });
  // Once per press. React runs an effect twice in development, and a second
  // request would only be refused as already waiting.
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    run.mutate();
  }, [run]);

  return (
    <div className="row-form" onClick={(e) => e.stopPropagation()}>
      {run.isPending || run.isIdle ? <span className="muted" role="status"><Spinner />Sending to web enrichment</span>
        : run.isError ? <span className="refusal" role="alert">{run.error.message}</span>
        // queue_enrichment returns false rather than raising when it declines,
        // so a quiet false has to be read out as a refusal. Saying "queued"
        // over one is how somebody waits for work that is not happening.
        : run.data === false ? <span className="refusal" role="status">Not queued. It is already waiting for web enrichment.</span>
        : <span className="muted" role="status">Queued for web enrichment. This can take hours.</span>}
      {run.isPending ? null : <button onClick={onDone}>Close</button>}
    </div>
  );
}

function FlagForm({ token, entityType, entityId, onDone }: {
  token: string; entityType: string; entityId: string; onDone: () => void;
}) {
  const [text, setText] = useState("");
  const [reason, setReason] = useState<string>("wrong_details");
  const [said, setSaid] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const queryClient = useQueryClient();

  const run = useMutation({
    mutationFn: () => callFunction<string>(token, "flag_record", {
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_reason: reason,
      p_note: text.trim(),
    }),
    onSuccess: () => {
      setRefused(false);
      setSaid("Flagged with your name and date. The record stays in the list.");
      queryClient.invalidateQueries({ queryKey: ["flags", entityType, entityId] });
      setText("");
      window.setTimeout(onDone, 2500);
    },
    onError: (e: Error) => { setRefused(true); setSaid(e.message); },
  });

  return (
    <div className="row-form" onClick={(e) => e.stopPropagation()}>
      <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="What is wrong">
        {FLAG_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
      {/* A visible label rather than a placeholder. James, 2026-09-22: a gray
          instruction inside a field reads as something the field contains. */}
      <label htmlFor="row-action-note">What is wrong</label>
      <input
        id="row-action-note"
        value={text}
        style={{ width: "24rem" }}
        autoFocus
        onChange={(e) => { setText(e.target.value); setSaid(null); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && text.trim().length >= 3) run.mutate();
          if (e.key === "Escape") onDone();
        }}
      />
      <button onClick={() => run.mutate()} disabled={text.trim().length < 3 || run.isPending}>
        {run.isPending ? <><Spinner />Sending</> : "Flag it"}
      </button>
      <button onClick={onDone}>Cancel</button>
      {said ? <span className={refused ? "refusal" : "muted"} role="status">{said}</span> : null}
    </div>
  );
}
